import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileCode, Code, Clock, Activity } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import { INITIAL_RC_DIM } from '../../utils/rechartsDefaults';

const CHART_TOOLTIP_STYLE = { backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' };
const CHART_AXIS_TICK = { fill: '#9CA3AF', fontSize: 12 };
const CHART_LABEL_STYLE = { color: '#F3F4F6' };
const CHART_LEGEND_STYLE = { fontSize: 11, color: '#9CA3AF' };
const CHART_LINE_FORMATTER = (v) => [`${v} lines`, 'Lines'];
const BAR_RADIUS_H = [0, 4, 4, 0];

const ChartCard = ({ icon: Icon, iconColor, title, children }) => (
  <Card className="bg-gray-900 border-gray-800">
    <CardHeader className="pb-2">
      <CardTitle className="text-base text-white flex items-center gap-2"><Icon className={`w-4 h-4 ${iconColor}`} />{title}</CardTitle>
    </CardHeader>
    <CardContent><div className="h-64">{children}</div></CardContent>
  </Card>
);

export const FileSizeCharts = ({ topFE, topBE }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    <ChartCard icon={FileCode} iconColor="text-emerald-500" title="Largest Frontend Components">
      <ResponsiveContainer width="100%" height="100%" initialDimension={INITIAL_RC_DIM}>
        <BarChart data={topFE} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" /><XAxis type="number" tick={CHART_AXIS_TICK} /><YAxis type="category" dataKey="name" width={120} tick={CHART_AXIS_TICK} />
          <RechartsTooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_LABEL_STYLE} formatter={CHART_LINE_FORMATTER} />
          <Bar dataKey="lines" fill="#10B981" radius={BAR_RADIUS_H} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
    <ChartCard icon={Code} iconColor="text-blue-500" title="Largest Backend Modules">
      <ResponsiveContainer width="100%" height="100%" initialDimension={INITIAL_RC_DIM}>
        <BarChart data={topBE} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" /><XAxis type="number" tick={CHART_AXIS_TICK} /><YAxis type="category" dataKey="name" width={120} tick={CHART_AXIS_TICK} />
          <RechartsTooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_LABEL_STYLE} formatter={CHART_LINE_FORMATTER} />
          <Bar dataKey="lines" fill="#3B82F6" radius={BAR_RADIUS_H} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  </div>
);

export const APIPerformanceCharts = ({ topEndpoints, timelineData }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    <ChartCard icon={Clock} iconColor="text-yellow-500" title="API Response Times (24h)">
      <ResponsiveContainer width="100%" height="100%" initialDimension={INITIAL_RC_DIM}>
        <BarChart data={topEndpoints} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" /><XAxis type="number" tick={CHART_AXIS_TICK} unit="ms" /><YAxis type="category" dataKey="name" width={130} tick={CHART_AXIS_TICK} />
          <RechartsTooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_LABEL_STYLE} />
          <Legend wrapperStyle={CHART_LEGEND_STYLE} />
          <Bar dataKey="avg_ms" fill="#F59E0B" name="Avg (ms)" radius={BAR_RADIUS_H} /><Bar dataKey="p95_ms" fill="#EF4444" name="P95 (ms)" radius={BAR_RADIUS_H} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
    {timelineData.length > 0 && (
      <ChartCard icon={Activity} iconColor="text-red-500" title="Request Volume & Errors (24h)">
        <ResponsiveContainer width="100%" height="100%" initialDimension={INITIAL_RC_DIM}>
          <LineChart data={timelineData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" /><XAxis dataKey="hour" tick={CHART_AXIS_TICK} /><YAxis tick={CHART_AXIS_TICK} />
            <RechartsTooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_LABEL_STYLE} />
            <Legend wrapperStyle={CHART_LEGEND_STYLE} />
            <Line type="monotone" dataKey="requests" stroke="#10B981" name="Requests" strokeWidth={2} /><Line type="monotone" dataKey="errors" stroke="#EF4444" name="Errors" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    )}
  </div>
);
